import {
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
} from "@floating-ui/react";
import { useEffect, useState } from "react";

import { formatDate } from "@smart/itops-utils-basic";

import { DatePicker } from "../date-picker";
import { BaseInput } from "../input/base";

type CustomDatePickerProps = {
  onClick?: () => void;
  onDateChange?: (date: Date) => void;
  selectedDate?: Date;
  disabledDays?: {
    before?: Date;
    after?: Date;
  };
  dataTestId?: string;
  unrestricted?: boolean;
  disabled?: boolean;
};

export const CustomDatePicker = ({
  onClick,
  selectedDate,
  onDateChange,
  disabledDays,
  dataTestId,
  unrestricted,
  disabled,
}: CustomDatePickerProps) => {
  const [inputValue, setInputValue] = useState<Date | undefined>(selectedDate);
  const [openDatePicker, setOpenDatePicker] = useState(false);

  useEffect(() => {
    setInputValue(selectedDate);
  }, [selectedDate]);

  const floating = useFloating({
    placement: "bottom",
    open: openDatePicker,
    onOpenChange: setOpenDatePicker,
    middleware: [
      shift({ padding: 10 }),
      offset(12),
      flip({ fallbackPlacements: ["top", "left", "right"] }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const interactions = useInteractions([
    useDismiss(floating.context),
    useFocus(floating.context),
  ]);

  const handleOnClick = () => {
    if (disabled) return;
    if (onClick) onClick();
    setOpenDatePicker(!openDatePicker);
  };
  return (
    <div ref={floating.refs.setReference}>
      <BaseInput
        onClick={handleOnClick}
        rightButtonIcon="regularCalendar"
        onRightButtonClick={handleOnClick}
        value={inputValue ? formatDate(inputValue) : undefined}
        className="hover:cursor-pointer [&>div>input]:hover:cursor-pointer"
        dataTestId={dataTestId}
        compact
        readOnly
        disabled={disabled}
      />
      {openDatePicker && (
        <div
          ref={floating.refs.setFloating}
          style={floating.floatingStyles}
          {...interactions.getFloatingProps()}
        >
          <DatePicker
            onDateChange={(date) => {
              setInputValue(date);
              if (onDateChange) onDateChange(date);
              setOpenDatePicker(false);
            }}
            selectedDate={selectedDate}
            disabledDays={disabledDays}
            dataTestId={dataTestId}
            unrestricted={unrestricted}
          />
        </div>
      )}
    </div>
  );
};
